/**
 * parseHoldAndWinState — Cleopatra's Fire Hold & Win, read off a response.
 *
 * The server sends the WHOLE H&W state on every step, in `features.bonus_data`
 * (contract §6), plus this step's deltas in `outcome.storage` (§5.2). This
 * turns the two into one plain object the client can draw from. It decides
 * nothing: every number, the zone, the collections and the royal are the
 * server's (GDD invariant 4).
 *
 * ── Rows: data vs screen (contract §14.1, the GDD wins) ──
 *
 * The data puts the zone on rows 0-3 and opens 4-7 downward. The GDD puts the
 * active zone on the BOTTOM four rows and opens upward. Until the API is
 * aligned, data -> screen is:
 *
 *   data row  0 1 2 3 | 4 5 6 7
 *   screen    4 5 6 7 | 3 2 1 0
 *
 * — the zone keeps its order (trigger coins keep their positions), the locked
 * rows count up from the one right above it. Everything here keeps the DATA
 * coordinates (`row`) and adds the screen one (`screenRow`); `screen` is the
 * whole grid already turned. Re-check the day a new draft or a real capture
 * with rows above 4 arrives.
 *
 * ── Phase ──
 *
 *   trigger   a `spin` / `freespin` that entered H&W (state `respin`)
 *   respin    a `respin` that did not settle
 *   settled   a `respin` with respins_left 0 — paid; `returnState` says where
 *             the round goes next (`closed` / `ready`, or `freespins`)
 *   restore   an `init` while `respin` is live. `restoreIntro` is true when it
 *             is the trigger response replayed (§9: respins 3, new_cells, and a
 *             spin-shaped outcome — `special_symbols` is null on respins).
 *             Deltas are NOT replayed on a restore (§9), so newCells and
 *             updatedCells are empty.
 *
 * ── Values: points -> money ──
 *
 * The server's cell values, collected values and payout are POINTS
 * (options.base_bet, 20, = 1x). The client shows money, so every value here is
 * converted: points × outcome.bet ÷ base_bet (outcome.bet is the round's bet in
 * subunits on every step). Play responses carry no `options`, so base_bet falls
 * back to 20 (the contract's). Without a bet the points are kept as they are.
 *
 * @param   {object} data The raw play/init response (`outcome`, `features`, `flow`).
 * @returns {object|null} null when the response carries no bonus_data.
 */

export const HOLD_AND_WIN_ROWS = 8;
export const HOLD_AND_WIN_BASE_ROWS = 4;

/**
 * Active rows for a count of symbols standing in the active zone (GDD "Determine
 * the Active Zone": 0–9 -> 4, 10–14 -> 5, 15–19 -> 6, 20–24 -> 7, 25+ -> 8 —
 * the server's `zone_thresholds`, identical). The server applies it at the
 * START of the next respin; the client reads it off the returned
 * `cells_in_zone` to show the row opening the moment the count is reached.
 */
export const ZONE_THRESHOLDS = [
  [0, 4],
  [10, 5],
  [15, 6],
  [20, 7],
  [25, 8],
];
export const rowsForCellsInZone = (cellsInZone) => {
  let rows = ZONE_THRESHOLDS[0][1];
  for (const [filledFrom, unlocked] of ZONE_THRESHOLDS) {
    if (cellsInZone >= filledFrom) rows = unlocked;
  }
  return rows;
};

/** Data row -> screen row (contract §14.1). */
export const toScreenRow = (dataRow) =>
  dataRow < HOLD_AND_WIN_BASE_ROWS
    ? dataRow + HOLD_AND_WIN_BASE_ROWS
    : HOLD_AND_WIN_ROWS - 1 - dataRow;

const toScreenGrid = (grid) =>
  Array.isArray(grid)
    ? grid.map((column) => {
        if (!Array.isArray(column)) return column;
        const out = new Array(column.length);
        column.forEach((cell, dataRow) => {
          out[toScreenRow(dataRow)] = cell;
        });
        return out;
      })
    : null;

const CONTRACT_BASE_BET = 20;

export const parseHoldAndWinState = (data) => {
  const bonus = data?.features?.bonus_data;
  if (!bonus) return null;

  const outcome = data?.outcome || {};
  const baseBet = data?.options?.base_bet || CONTRACT_BASE_BET;
  const factor = outcome.bet > 0 ? outcome.bet / baseBet : 1;
  const money = (points) =>
    typeof points === "number" ? Math.round(points * factor) : points;
  const moneyGrid = (grid) =>
    Array.isArray(grid)
      ? grid.map((column) => (Array.isArray(column) ? column.map(money) : column))
      : grid;
  const storage = outcome.storage || {};
  const command = data?.flow?.command ?? null;
  const returnState = data?.flow?.state ?? null;
  const respinsLeft = bonus.respins_left ?? null;

  let phase;
  if (command === "spin" || command === "freespin") phase = "trigger";
  else if (command === "respin") phase = respinsLeft === 0 ? "settled" : "respin";
  else phase = "restore";

  const isRestore = phase === "restore";
  const restoreIntro =
    isRestore &&
    respinsLeft === 3 &&
    Array.isArray(storage.new_cells) &&
    outcome.special_symbols !== null &&
    outcome.special_symbols !== undefined;

  const newCells = isRestore
    ? []
    : (storage.new_cells || []).map(([reel, row, value, kind]) => ({
        reel,
        row,
        screenRow: toScreenRow(row),
        value: money(value),
        kind,
      }));
  const updatedCells = isRestore
    ? []
    : (storage.updated_cells || []).map(([reel, row, value]) => ({
        reel,
        row,
        screenRow: toScreenRow(row),
        value: money(value),
      }));

  return {
    phase,
    restoreIntro,
    command,
    returnState,
    respinsLeft,
    unlockedRows: bonus.unlocked_rows ?? HOLD_AND_WIN_BASE_ROWS,
    cellsInZone: bonus.cells_in_zone ?? 0,
    accumulatedPayout: money(bonus.accumulated_payout ?? 0),
    grandDrawn: !!bonus.grand_drawn,
    royalJackpot: !!bonus.royal_jackpot,
    randomPot: !!bonus.random_pot,
    // Data coordinates, [column][row].
    kinds: bonus.cell_kinds ?? null,
    values: moneyGrid(bonus.cell_values ?? null),
    scarabMultipliers: bonus.scarab_multipliers ?? null,
    // The same three grids with the rows turned for the screen (§14.1).
    screen: {
      kinds: toScreenGrid(bonus.cell_kinds),
      values: toScreenGrid(moneyGrid(bonus.cell_values)),
      // Raw points — matched against options.jackpots for the jackpot coin.
      points: toScreenGrid(bonus.cell_values),
      scarabMultipliers: toScreenGrid(bonus.scarab_multipliers),
    },
    // Reveal order; null until the settling respin (then [] if none).
    scarabs: Array.isArray(bonus.scarabs)
      ? bonus.scarabs.map(([reel, row, multiplier]) => ({
          reel,
          row,
          screenRow: toScreenRow(row),
          multiplier,
        }))
      : null,
    newCells,
    updatedCells,
    winLimitReached: !!storage.win_limit_reached,
    // This step's credit, subunits, after the cap — 0 until it settles.
    win: outcome.win ?? 0,
  };
};
